import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useFocusEffect, useLocalSearchParams, useNavigation, useRouter } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { colors, radius, spacing, typography } from "@/lib/theme";
import type { Course } from "@/lib/training/types";
import {
    getTrainingBundle,
    type BundleProgress,
    type BundleWindow,
    type TrainingBundle
} from "@/lib/training-api";

/** `YYYY-MM-DD` → "Oct 31, 2026", read as a calendar date (no timezone shift). */
function fmtDate(iso: string): string {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC"
    });
}

/**
 * The nurse's training list.
 *
 * The shell mirrors app/compliance/index.tsx — same SafeAreaView edges, the
 * same back-arrow top bar, the same display/subheader pairing — so training
 * reads as part of the app rather than a screen of its own. Without the safe
 * area the title renders under the status bar and dynamic island.
 *
 * Content and completion both come from the API; nothing is compiled in, so
 * publishing new content needs no app release.
 */
export default function BundleDetailScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const { bundleSlug } = useLocalSearchParams<{ bundleSlug: string }>();
    const { user } = useAuth();
    const rnId = user?.rnId ?? null;

    const [bundle, setBundle] = useState<TrainingBundle | null>(null);
    const [progress, setProgress] = useState<BundleProgress | null>(null);
    const [renewal, setRenewal] = useState<BundleWindow | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!rnId) {
            return;
        }
        setError(null);
        try {
            const r = await getTrainingBundle(rnId, bundleSlug);
            setBundle(r.bundle);
            setProgress(r.progress);
            setRenewal(r.window ?? null);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Couldn't load your training");
        } finally {
            setLoading(false);
        }
    }, [rnId, bundleSlug]);

    // `rnId` starts null while auth rehydrates from storage, so this fires
    // again once it lands.
    useEffect(() => {
        void load();
    }, [load]);

    // Refresh on focus so returning from a finished course shows it ticked.
    useFocusEffect(
        useCallback(() => {
            void load();
        }, [load])
    );

    const done = new Set(progress?.completedSlugs ?? []);
    const nextCourse = bundle?.content.courses.find((c) => !done.has(c.slug));

    return (
        <SafeAreaView edges={["top", "bottom"]} style={styles.container}>
            <View style={styles.topBar}>
                <TouchableOpacity
                    style={styles.backArrow}
                    hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                    onPress={() => {
                        // Opened straight from another screen (the Compliance
                        // card, the blocked-visit dialog): go back there, so
                        // she sees the item renewed or can retry the visit.
                        const own = navigation.getState();
                        const parent = navigation.getParent()?.getState();
                        const previous =
                            parent && parent.index > 0
                                ? parent.routes[parent.index - 1]?.name
                                : undefined;
                        if (own?.index === 0 && previous && previous !== "(tabs)") {
                            router.back();
                            return;
                        }
                        // Same reasoning as the course player: a deep link
                        // leaves the tabs underneath, so back() would drop to
                        // Home rather than the training list.
                        router.dismissTo("/training");
                    }}
                >
                    <MaterialCommunityIcons
                        name="chevron-left"
                        size={28}
                        color={colors.textPrimary}
                    />
                </TouchableOpacity>
            </View>

            <ScrollView contentContainerStyle={styles.scroll}>
                <Text style={styles.header}>{bundle?.title ?? "Training"}</Text>
                {bundle ? (
                    <Text style={styles.subheader}>{`${bundle.content.courses.length} ${
                        bundle.content.courses.length === 1 ? "course" : "courses"
                    }`}</Text>
                ) : null}
                {/* Yearly training: when it is due (after finishing, when it is next due). */}
                {renewal?.dueDate && progress && !progress.isComplete ? (
                    <Text style={styles.dueLine}>
                        {`Yearly requirement · complete by ${fmtDate(renewal.dueDate)}`}
                    </Text>
                ) : null}
                {renewal?.dueDate && progress?.isComplete && !renewal.open ? (
                    <Text style={styles.subheader}>
                        {`Done for this year · next due ${fmtDate(renewal.dueDate)}`}
                    </Text>
                ) : null}

                {loading && (
                    <View style={styles.center}>
                        <ActivityIndicator size="large" color={colors.teal400} />
                    </View>
                )}

                {error && !loading && (
                    <View style={styles.center}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={44}
                            color={colors.error500}
                        />
                        <Text style={styles.errorText}>{error}</Text>
                        <TouchableOpacity onPress={() => void load()}>
                            <Text style={styles.retry}>Try again</Text>
                        </TouchableOpacity>
                    </View>
                )}

                {!loading && !error && bundle && progress && (
                    <>
                        <View style={styles.hero}>
                            <Text style={styles.heroTitle}>
                                {progress.isComplete ? "Complete" : "Your progress"}
                            </Text>
                            <Text style={styles.heroMeta}>
                                {progress.completed} of {progress.total} courses complete
                            </Text>

                            <View style={styles.bar}>
                                <View
                                    style={[
                                        styles.barFill,
                                        {
                                            width: `${
                                                progress.total > 0
                                                    ? (progress.completed / progress.total) * 100
                                                    : 0
                                            }%`
                                        }
                                    ]}
                                />
                            </View>

                            {progress.isComplete ? (
                                <View style={styles.doneRow}>
                                    <MaterialCommunityIcons
                                        name="check-circle"
                                        size={18}
                                        color={colors.teal400}
                                    />
                                    <Text style={styles.doneText}>All courses complete</Text>
                                </View>
                            ) : nextCourse ? (
                                <TouchableOpacity
                                    style={styles.cta}
                                    activeOpacity={0.85}
                                    onPress={() =>
                                        router.push(`/training/${bundleSlug}/${nextCourse.slug}`)
                                    }
                                >
                                    <Text style={styles.ctaText}>
                                        {progress.completed > 0 ? "Continue" : "Start"}
                                    </Text>
                                    <MaterialCommunityIcons
                                        name="arrow-right"
                                        size={18}
                                        color="#FFFFFF"
                                    />
                                </TouchableOpacity>
                            ) : null}
                        </View>

                        <Text style={styles.sectionLabel}>COURSES</Text>

                        {bundle.content.courses.map((course, i) => (
                            <CourseRow
                                key={course.slug}
                                n={i + 1}
                                course={course}
                                done={done.has(course.slug)}
                                onPress={() =>
                                    router.push(`/training/${bundleSlug}/${course.slug}`)
                                }
                            />
                        ))}
                    </>
                )}
            </ScrollView>
        </SafeAreaView>
    );
}

function CourseRow({
    n,
    course,
    done,
    onPress
}: {
    n: number;
    course: Course;
    done: boolean;
    onPress: () => void;
}) {
    const mins =
        course.estMin ??
        Math.max(1, Math.round(course.screens.reduce((a, s) => a + s.timeSec, 0) / 60));

    return (
        <TouchableOpacity style={styles.row} onPress={onPress} activeOpacity={0.7}>
            <View style={[styles.rowN, done && styles.rowNDone]}>
                {done ? (
                    <MaterialCommunityIcons name="check" size={16} color="#FFFFFF" />
                ) : (
                    <Text style={styles.rowNText}>{n}</Text>
                )}
            </View>
            <View style={styles.rowBody}>
                <Text style={styles.rowTitle}>{course.title}</Text>
                <Text style={styles.rowMeta}>{done ? "Complete" : `~${mins} min`}</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={22} color={colors.textTertiary} />
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    topBar: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: spacing.sm,
        paddingTop: spacing.xs
    },
    backArrow: {
        width: 44,
        height: 44,
        alignItems: "center",
        justifyContent: "center"
    },
    scroll: {
        paddingHorizontal: spacing.screenPadding,
        paddingBottom: spacing["3xl"],
        gap: spacing.md
    },
    header: {
        ...typography.display,
        color: colors.textPrimary
    },
    subheader: {
        ...typography.body,
        color: colors.textSecondary,
        marginBottom: spacing.sm
    },
    dueLine: {
        ...typography.bodySmall,
        color: colors.warning,
        marginBottom: spacing.sm
    },
    center: {
        alignItems: "center",
        paddingVertical: spacing["3xl"],
        gap: spacing.sm
    },
    errorText: {
        ...typography.body,
        color: colors.textSecondary,
        textAlign: "center"
    },
    retry: {
        ...typography.body,
        color: colors.teal600,
        fontWeight: "700"
    },

    hero: {
        backgroundColor: colors.teal700,
        borderRadius: radius.lg,
        padding: spacing.lg,
        gap: spacing.sm
    },
    heroTitle: {
        ...typography.h2,
        color: "#FFFFFF"
    },
    heroMeta: {
        ...typography.bodySmall,
        color: colors.teal100
    },
    bar: {
        height: 8,
        borderRadius: 4,
        backgroundColor: "rgba(255,255,255,0.22)",
        overflow: "hidden"
    },
    barFill: {
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.teal400
    },
    cta: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        backgroundColor: colors.teal500,
        borderRadius: radius.md,
        paddingVertical: 13,
        marginTop: spacing.xs
    },
    ctaText: {
        ...typography.title,
        color: "#FFFFFF"
    },
    doneRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: spacing.xs
    },
    doneText: {
        ...typography.title,
        color: "#FFFFFF"
    },

    sectionLabel: {
        ...typography.label,
        color: colors.textTertiary,
        marginTop: spacing.md
    },

    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.border,
        padding: spacing.md
    },
    rowN: {
        width: 32,
        height: 32,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.teal50
    },
    rowNDone: { backgroundColor: colors.teal500 },
    rowNText: {
        ...typography.title,
        color: colors.teal600
    },
    rowBody: { flex: 1, minWidth: 0 },
    rowTitle: {
        ...typography.title,
        color: colors.textPrimary
    },
    rowMeta: {
        ...typography.bodySmall,
        color: colors.textSecondary,
        marginTop: 2
    }
});
